import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  GridLegacy as Grid,
  Alert,
  CircularProgress,
  useTheme,
  Stack,
  Avatar,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  Fade,
} from '@mui/material';
import {
  AutoGraph,
  GetApp as DownloadIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
  Download,
  Assessment,
  Star,
  Timer as TimerIcon,
  VerticalAlignBottom as ScrollIcon,
  Check as CompletedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import productService from '../services/productService';
import pointsService from '../services/pointsService';
import { Product, LoyaltyLevel } from '../types/productTypes';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import FeatureGuard from '../components/FeatureGuard';

const MotionCard = motion(Card);

interface UserData {
  loyaltyLevel: LoyaltyLevel;
  displayName: string;
  points: number;
}

const ExpertAdvisorsPage: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState<Record<string, boolean>>({});
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [currentProductForTerms, setCurrentProductForTerms] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, [currentUser]);

  // Handle terms modal timer and reset
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (termsModalOpen) {
      // Reset states when modal opens
      setHasScrolledToBottom(false);
      setTimeElapsed(false);
      setReadingProgress(0);
      
      // Start 30-second timer
      timer = setTimeout(() => {
        setTimeElapsed(true);
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [termsModalOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load products
      const allProducts = await productService.getProducts({ isActive: true });
      setProducts(allProducts);

      // Load user data if authenticated
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userProfile = userDoc.data();
          
          // Get loyalty level and progress
          const progress = await pointsService.calculateXmLevel(currentUser.uid, userProfile);
          
          setUserData({
            loyaltyLevel: (progress?.currentXmLevel === 'none' ? 'bronze' : progress?.currentXmLevel) || 'bronze',
            displayName: userProfile.displayName || currentUser.displayName || 'User',
            points: progress?.monthlyPoints || 0,
          });
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load Expert Advisors');
    } finally {
      setLoading(false);
    }
  };

  const handleTermsChange = (productId: string, accepted: boolean) => {
    if (accepted && !acceptedTerms[productId]) {
      // If user is trying to accept terms but hasn't completed the process, open modal
      setCurrentProductForTerms(productId);
      setTermsModalOpen(true);
    } else if (!accepted) {
      // If user is unchecking, allow it
      setAcceptedTerms(prev => ({
        ...prev,
        [productId]: false
      }));
    }
  };

  const handleTermsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrolled = (scrollTop / (scrollHeight - clientHeight)) * 100;
    
    setReadingProgress(Math.min(scrolled, 100));
    
    // Check if user has scrolled to bottom (within 5px tolerance)
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setHasScrolledToBottom(true);
    }
  };

  const canAcceptTerms = hasScrolledToBottom && timeElapsed;

  const handleDownload = async (product: Product) => {
    if (!currentUser || !userData) {
      setError('Please log in to download Expert Advisors');
      return;
    }

    // Check access level
    if (!productService.canUserAccessProduct(product, userData.loyaltyLevel)) {
      setSelectedProduct(product);
      setDialogOpen(true);
      return;
    }

    // Check if terms are accepted
    if (!acceptedTerms[product.id]) {
      setCurrentProductForTerms(product.id);
      setTermsModalOpen(true);
      setError('Please read and accept the terms and conditions to download this Expert Advisor');
      return;
    }

    try {
      setDownloading(product.id);
      setError(null);

      // Record the download
      await productService.recordDownload(
        product.id,
        product.name,
        currentUser.uid,
        currentUser.email || '',
        userData.displayName,
        userData.loyaltyLevel
      );

      // Initiate download with original filename
      if (product.downloadUrl) {
        try {
          // Fetch the file to get original filename and trigger download
          const response = await fetch(product.downloadUrl);
          const blob = await response.blob();
          
          // Try to get original filename from Content-Disposition header or URL
          let filename = product.name;
          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          } else {
            // Extract filename from URL if no Content-Disposition header
            const urlParts = product.downloadUrl.split('/');
            const urlFilename = urlParts[urlParts.length - 1];
            if (urlFilename && urlFilename.includes('.')) {
              filename = decodeURIComponent(urlFilename.split('?')[0]);
            }
          }

          // Clean up Firebase storage filename format (remove timestamp prefix)
          // Format: products_downloads_1750167345949_Trading With The ProfXssor.pdf
          // Extract: Trading With The ProfXssor.pdf
          const cleanFilename = (name: string) => {
            // Try multiple patterns to clean the filename
            
            // Pattern 1: products_downloads_timestamp_actualname
            let match = name.match(/^products_downloads_\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // Pattern 2: products/downloads/timestamp_actualname (path format)
            match = name.match(/^products\/downloads\/\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // Pattern 3: any_timestamp_actualname
            match = name.match(/^\w+_\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // Pattern 4: just timestamp_actualname
            match = name.match(/^\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // If filename contains the product name, try to extract based on that
            if (name.includes(product.name)) {
              const productNameIndex = name.indexOf(product.name);
              if (productNameIndex > 0) {
                const extracted = name.substring(productNameIndex);
                return extracted;
              }
            }
            
            return name;
          };

          filename = cleanFilename(filename);

          // Create blob URL and trigger download
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up blob URL
          URL.revokeObjectURL(blobUrl);

          setSuccess(`${filename} download started successfully!`);
        } catch (downloadError) {
          console.error('Download error:', downloadError);
          // Fallback to simple download if fetch fails
          
          // Clean filename for fallback too
          const cleanFilename = (name: string) => {
            // Try multiple patterns to clean the filename
            
            // Pattern 1: products_downloads_timestamp_actualname
            let match = name.match(/^products_downloads_\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // Pattern 2: products/downloads/timestamp_actualname (path format)
            match = name.match(/^products\/downloads\/\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // Pattern 3: any_timestamp_actualname
            match = name.match(/^\w+_\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // Pattern 4: just timestamp_actualname
            match = name.match(/^\d+_(.+)$/);
            if (match && match[1]) {
              return match[1];
            }
            
            // If filename contains the product name, try to extract based on that
            if (name.includes(product.name)) {
              const productNameIndex = name.indexOf(product.name);
              if (productNameIndex > 0) {
                const extracted = name.substring(productNameIndex);
                return extracted;
              }
            }
            
            return name;
          };

          // Try to extract clean filename from URL
          let fallbackFilename = product.name;
          if (product.downloadUrl) {
            const urlParts = product.downloadUrl.split('/');
            const urlFilename = urlParts[urlParts.length - 1];
            if (urlFilename && urlFilename.includes('.')) {
              const decodedFilename = decodeURIComponent(urlFilename.split('?')[0]);
              fallbackFilename = cleanFilename(decodedFilename);
            }
          }
          
          const link = document.createElement('a');
          link.href = product.downloadUrl;
          link.download = fallbackFilename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setSuccess(`${fallbackFilename} download started successfully!`);
        }
      } else {
        setError('Download file not available');
      }
    } catch (err) {
      console.error('Error downloading product:', err);
      setError('Failed to download Expert Advisor. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const getLevelColor = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#666';
    }
  };

  const getLevelIcon = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '';
    }
  };

  const getLevelProgress = (userLevel: LoyaltyLevel, requiredLevels: LoyaltyLevel[]) => {
    const levelOrder: LoyaltyLevel[] = ['bronze', 'silver', 'gold'];
    const userIndex = levelOrder.indexOf(userLevel);
    const highestRequired = Math.max(...requiredLevels.map(level => levelOrder.indexOf(level)));
    
    if (userIndex >= highestRequired) return 100;
    return ((userIndex + 1) / (highestRequired + 1)) * 100;
  };

  const canUserAccess = (product: Product) => {
    return userData && productService.canUserAccessProduct(product, userData.loyaltyLevel);
  };

  const getNextRequiredLevel = (product: Product) => {
    if (!userData) return product.loyaltyLevels[0];
    
    const levelOrder: LoyaltyLevel[] = ['bronze', 'silver', 'gold'];
    const userIndex = levelOrder.indexOf(userData.loyaltyLevel);
    
    return product.loyaltyLevels.find(level => 
      levelOrder.indexOf(level) > userIndex
    ) || product.loyaltyLevels[0];
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
            <Typography sx={{ ml: 2 }}>Loading Expert Advisors...</Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <AutoGraph 
                sx={{ 
                  fontSize: '4rem',
                  color: theme.palette.primary.main,
                  filter: 'drop-shadow(0 4px 12px rgba(255, 215, 0, 0.3))'
                }} 
              />
            </Box>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, #FFA726)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 700,
              }}
            >
              Expert Advisors Library
            </Typography>
          </motion.div>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Products Grid */}
        <Grid container spacing={3}>
          {products.map((product, index) => {
            const hasAccess = canUserAccess(product);
            const isDownloading = downloading === product.id;
            const nextLevel = getNextRequiredLevel(product);
            const progress = userData ? getLevelProgress(userData.loyaltyLevel, product.loyaltyLevels) : 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <MotionCard
                    sx={{
                      height: '100%',
                      minHeight: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      border: hasAccess ? `2px solid ${theme.palette.success.main}` : `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      ...(hasAccess && {
                        boxShadow: `0 4px 20px ${theme.palette.success.main}20`,
                      }),
                    }}
                    whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)' }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Access Status Badge */}
                    {!hasAccess && (
                      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                        <Tooltip title={`Requires ${product.loyaltyLevels.join(' or ')} level`}>
                          <Chip
                            icon={<LockIcon />}
                            label="Locked"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </Box>
                    )}

                    {/* Product Image */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '75%', // 4:3 aspect ratio (bigger image space)
                        overflow: 'hidden',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                      }}
                    >
                      {product.imageUrl ? (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          alt={product.name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <AutoGraph sx={{ 
                            fontSize: { xs: '3rem', sm: '4rem' }, 
                            color: theme.palette.grey[400] 
                          }} />
                        </Box>
                      )}
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                      {/* Product Name */}
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>

                      {/* Product Description */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                        {product.description}
                      </Typography>

                      {/* Required Levels */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Required Loyalty Level:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {product.loyaltyLevels
                            .sort((a, b) => {
                              const order: LoyaltyLevel[] = ['bronze', 'silver', 'gold'];
                              return order.indexOf(a) - order.indexOf(b);
                            })
                            .map((level) => (
                              <Chip
                                key={level}
                                label={`${getLevelIcon(level)} ${level}`}
                                size="small"
                                sx={{ 
                                  bgcolor: `${getLevelColor(level)}20`,
                                  color: getLevelColor(level),
                                  textTransform: 'capitalize',
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                        </Box>
                      </Box>

                      {/* Access Progress (for locked products) */}
                      {!hasAccess && userData && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Your Progress
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(progress)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: theme.palette.grey[200],
                              '& .MuiLinearProgress-bar': {
                                bgcolor: getLevelColor(nextLevel),
                                borderRadius: 3,
                              },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Upgrade to {nextLevel} to unlock
                          </Typography>
                        </Box>
                      )}

                      {/* Download Stats */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Download sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                          <Typography variant="caption" color="text.secondary">
                            {product.downloadCount} downloads
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      {/* Spacer to push buttons to bottom */}
                      <Box sx={{ flexGrow: 1 }} />

                      {/* Action Buttons */}
                      <Stack spacing={2}>
                        {hasAccess && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={acceptedTerms[product.id] || false}
                                onChange={(e) => handleTermsChange(product.id, e.target.checked)}
                                size="small"
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&.Mui-checked': {
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography variant="caption" color="text.secondary">
                                I accept the{' '}
                                <Link
                                  component="button"
                                  variant="caption"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setTermsModalOpen(true);
                                  }}
                                  sx={{
                                    textDecoration: 'underline',
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                  }}
                                >
                                  terms and conditions
                                </Link>
                              </Typography>
                            }
                            sx={{ ml: 0 }}
                          />
                        )}

                        {hasAccess ? (
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                            onClick={() => handleDownload(product)}
                            disabled={isDownloading || !currentUser || !acceptedTerms[product.id]}
                            sx={{
                              bgcolor: theme.palette.success.main,
                              '&:hover': { bgcolor: theme.palette.success.dark },
                              '&:disabled': {
                                bgcolor: theme.palette.grey[400],
                                color: theme.palette.common.white,
                                opacity: 0.6,
                                '& .MuiCircularProgress-root': {
                                  color: theme.palette.common.white,
                                },
                              },
                            }}
                          >
                            {isDownloading ? 'Downloading...' : 'Download'}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<LockIcon />}
                            onClick={() => {
                              setSelectedProduct(product);
                              setDialogOpen(true);
                            }}
                            sx={{
                              borderColor: theme.palette.warning.main,
                              color: theme.palette.warning.main,
                              '&:hover': {
                                borderColor: theme.palette.warning.dark,
                                bgcolor: `${theme.palette.warning.main}10`,
                              },
                            }}
                          >
                            Requires {nextLevel} Level
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </MotionCard>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {/* Empty State */}
        {products.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <AutoGraph sx={{ fontSize: '6rem', color: theme.palette.grey[300], mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No Expert Advisors Available
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Expert Advisors will appear here once they are published by our team.
            </Typography>
          </Box>
        )}

        {/* Product Detail Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          {selectedProduct && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {selectedProduct.imageUrl && (
                    <Avatar
                      src={selectedProduct.imageUrl}
                      sx={{ 
                        width: 48, 
                        height: 48,
                        borderRadius: 1,
                      }}
                      variant="rounded"
                    />
                  )}
                  <Box>
                    <Typography variant="h6">{selectedProduct.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {selectedProduct.loyaltyLevels
                        .sort((a, b) => {
                          const order: LoyaltyLevel[] = ['bronze', 'silver', 'gold'];
                          return order.indexOf(a) - order.indexOf(b);
                        })
                        .map((level) => (
                          <Chip
                            key={level}
                            label={`${getLevelIcon(level)} ${level}`}
                            size="small"
                            sx={{ 
                              bgcolor: `${getLevelColor(level)}20`,
                              color: getLevelColor(level),
                              textTransform: 'capitalize',
                            }}
                          />
                        ))}
                    </Box>
                  </Box>
                </Box>
              </DialogTitle>
              
              <DialogContent>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {selectedProduct.description}
                </Typography>

                {!canUserAccess(selectedProduct) && userData && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Upgrade Required:</strong> This Expert Advisor requires {' '}
                      {selectedProduct.loyaltyLevels.join(' or ')} level access. 
                      Your current level is {userData.loyaltyLevel}.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Earn more XM Points through trading to unlock higher loyalty levels and access premium Expert Advisors.
                    </Typography>
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Download sx={{ color: theme.palette.text.secondary }} />
                    <Typography variant="body2">
                      {selectedProduct.downloadCount} downloads
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star sx={{ color: theme.palette.warning.main }} />
                    <Typography variant="body2">
                      Expert Advisor
                    </Typography>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setDialogOpen(false)}>
                  Close
                </Button>
                {canUserAccess(selectedProduct) && (
                  <Button
                    variant="contained"
                    startIcon={downloading === selectedProduct.id ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={() => {
                      handleDownload(selectedProduct);
                      setDialogOpen(false);
                    }}
                    disabled={downloading === selectedProduct.id || !currentUser}
                  >
                    {downloading === selectedProduct.id ? 'Downloading...' : 'Download EA'}
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Terms and Conditions Modal */}
        <Dialog
          open={termsModalOpen}
          onClose={() => {
            setTermsModalOpen(false);
            setCurrentProductForTerms(null);
          }}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" component="h2">
                Expert Advisor Terms and Conditions
              </Typography>
              
              {/* Reading Progress Indicators */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {timeElapsed ? (
                    <CompletedIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  ) : (
                    <TimerIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                  )}
                  <Typography variant="caption" color={timeElapsed ? 'success.main' : 'text.secondary'}>
                    {timeElapsed ? 'Time Complete' : 'Reading Time'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {hasScrolledToBottom ? (
                    <CompletedIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  ) : (
                    <ScrollIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                  )}
                  <Typography variant="caption" color={hasScrolledToBottom ? 'success.main' : 'text.secondary'}>
                    {hasScrolledToBottom ? 'Scroll Complete' : 'Scroll to End'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Progress Bar */}
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={readingProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: readingProgress === 100 ? 'success.main' : 'primary.main',
                    borderRadius: 3,
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Scroll Progress: {Math.round(readingProgress)}%
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent dividers onScroll={handleTermsScroll}>
            <Box sx={{ '& > *': { mb: 3 } }}>
              {/* Software License Agreement */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  1. Software License Agreement
                </Typography>
                <Typography variant="body2" paragraph>
                  By downloading and using our Expert Advisors (EAs), you agree to be bound by this license agreement. 
                  This software is licensed, not sold, and remains the intellectual property of our organization.
                </Typography>
                <Typography variant="body2" paragraph>
                  You are granted a non-exclusive, non-transferable license to use the EA software solely for your personal trading activities. 
                  You may not distribute, resell, reverse engineer, or modify the software without explicit written permission.
                </Typography>
              </Box>

              {/* Usage Terms */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  2. Software Usage Policy
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Permitted Use:</strong> The EA software is intended for use on MetaTrader platforms for automated trading purposes. 
                  You may install the software on multiple devices for your personal use only.
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Prohibited Activities:</strong> You may not use the software for any illegal activities, market manipulation, 
                  or in violation of your broker's terms of service. Commercial redistribution or sharing of account credentials is strictly prohibited.
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Account Responsibility:</strong> You are solely responsible for the configuration, monitoring, and consequences 
                  of using the EA software on your trading accounts.
                </Typography>
              </Box>

              {/* Risk Disclaimers */}
              <Box>
                <Typography variant="h6" gutterBottom color="error">
                  3. Risk Disclaimers and Warnings
                </Typography>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    HIGH RISK WARNING: Trading in financial markets involves substantial risk and may not be suitable for all investors.
                  </Typography>
                </Alert>
                
                <Typography variant="body2" paragraph>
                  <strong>Automated Trading Risks:</strong> Expert Advisors are automated trading systems that execute trades without human intervention. 
                  Past performance does not guarantee future results. Market conditions can change rapidly, and EAs may not adapt to all market scenarios.
                </Typography>
                
                <Typography variant="body2" paragraph>
                  <strong>Potential Losses:</strong> You may lose all or more than your initial investment. Only trade with money you can afford to lose. 
                  Leverage amplifies both profits and losses, and automated systems can execute multiple trades rapidly, potentially increasing exposure.
                </Typography>
                
                <Typography variant="body2" paragraph>
                  <strong>Technical Risks:</strong> EAs depend on stable internet connections, platform functionality, and broker execution. 
                  Technical failures, system outages, or connectivity issues may result in missed opportunities or unexpected losses.
                </Typography>
                
                <Typography variant="body2" paragraph>
                  <strong>Market Risks:</strong> Financial markets are volatile and unpredictable. Economic events, news releases, 
                  and market sentiment can cause rapid price movements that may not be favorable to your EA's strategy.
                </Typography>
              </Box>

              {/* Performance Disclaimer */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  4. Performance and Results Disclaimer
                </Typography>
                <Typography variant="body2" paragraph>
                  No guarantee is made regarding the profitability or performance of any Expert Advisor. Historical backtesting results 
                  are hypothetical and do not represent actual trading results. Market conditions, spreads, slippage, and execution differences 
                  can significantly impact real-world performance.
                </Typography>
                <Typography variant="body2" paragraph>
                  We strongly recommend testing any EA on a demo account before live trading and starting with minimal position sizes 
                  to understand the software's behavior in current market conditions.
                </Typography>
              </Box>

              {/* Support and Warranty */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  5. Support and Warranty Limitations
                </Typography>
                <Typography variant="body2" paragraph>
                  The software is provided "as is" without warranties of any kind. We do not guarantee compatibility with all broker platforms, 
                  market conditions, or trading environments. Support may be provided at our discretion but is not guaranteed.
                </Typography>
                <Typography variant="body2" paragraph>
                  We are not responsible for any direct, indirect, incidental, or consequential damages resulting from the use or inability 
                  to use the EA software, including but not limited to trading losses, data loss, or system failures.
                </Typography>
              </Box>

              {/* Compliance and Legal */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  6. Legal Compliance and Jurisdiction
                </Typography>
                <Typography variant="body2" paragraph>
                  You are responsible for ensuring that your use of the EA software complies with all applicable laws and regulations 
                  in your jurisdiction. Some countries may have restrictions on automated trading or financial software.
                </Typography>
                <Typography variant="body2" paragraph>
                  This agreement is governed by the laws of the jurisdiction where our organization is registered. 
                  Any disputes shall be resolved through binding arbitration in accordance with applicable arbitration rules.
                </Typography>
              </Box>

              {/* Acceptance */}
              <Box>
                <Alert severity="info">
                  <Typography variant="body2" fontWeight="bold">
                    By checking the "I accept the terms and conditions" box and downloading the Expert Advisor, 
                    you acknowledge that you have read, understood, and agree to be bound by all terms stated above.
                  </Typography>
                </Alert>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
            {/* Requirements Status */}
            {!canAcceptTerms && (
              <Alert severity="info" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  <strong>Please complete both requirements:</strong>
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" color={timeElapsed ? 'success.main' : 'text.secondary'}>
                    • {timeElapsed ? '✓' : '○'} Spend at least 30 seconds reading the terms
                  </Typography>
                  <Typography variant="caption" color={hasScrolledToBottom ? 'success.main' : 'text.secondary'}>
                    • {hasScrolledToBottom ? '✓' : '○'} Scroll to the bottom of the document
                  </Typography>
                </Box>
              </Alert>
            )}

            {canAcceptTerms && (
              <Alert severity="success" sx={{ width: '100%' }}>
                <Typography variant="body2" fontWeight="bold">
                  ✓ Requirements completed! You may now accept the terms.
                </Typography>
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => {
                  setTermsModalOpen(false);
                  setCurrentProductForTerms(null);
                }}
                variant="outlined"
                size="large"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  if (canAcceptTerms && currentProductForTerms) {
                    // Accept the terms for the current product
                    setAcceptedTerms(prev => ({
                      ...prev,
                      [currentProductForTerms]: true
                    }));
                  }
                  setTermsModalOpen(false);
                  setCurrentProductForTerms(null);
                }}
                variant="contained"
                size="large"
                disabled={!canAcceptTerms}
                sx={{ 
                  minWidth: 120,
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                {canAcceptTerms ? 'I Understand & Accept' : 'Complete Requirements'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

function ExpertAdvisorsFeature() {
  return (
    <FeatureGuard featureId="expert_advisors">
      <ExpertAdvisorsPage />
    </FeatureGuard>
  );
}

export const ExpertAdvisors = ExpertAdvisorsFeature;

export default ExpertAdvisorsFeature; 